<template>
  <div class="ap-page">
    <!-- Intro and search -->
    <section class="ap-hero tk-hero">
      <div class="ap-hero-copy">
        <span class="ap-kicker">Track application</span>
        <h1 class="ap-hero-title">Where is my <em>application?</em></h1>
        <p class="ap-hero-sub">Enter your application number to see its status, who has it now and what has happened so far.</p>
      </div>

      <form class="tk-search" @submit.prevent="search()">
        <label class="tk-search-box">
          <v-icon small>mdi-magnify</v-icon>
          <input
            v-model.trim="query"
            type="text"
            class="tk-search-input"
            placeholder="Application number, e.g. DDD/1058/2026"
            autocomplete="off"
            :disabled="loading"
            aria-label="Application number"
          />
          <button v-if="query" type="button" class="tk-search-clear" aria-label="Clear" @click="reset">
            <v-icon small>mdi-close-circle</v-icon>
          </button>
        </label>
        <button type="submit" class="ap-btn ap-btn--light" :disabled="loading || !query">
          <v-icon small>{{ loading ? "mdi-loading" : "mdi-map-marker-path" }}</v-icon>
          {{ loading ? "Looking up…" : "Track" }}
        </button>
      </form>
    </section>

    <!-- Quick picks -->
    <div v-if="recent.length && !app" class="tk-recent">
      <span class="tk-recent-label">Your recent applications</span>
      <button v-for="r in recent" :key="r._id" type="button" class="tk-recent-chip" @click="search(r.applicationNo)">
        <span class="tk-recent-no">{{ r.applicationNo }}</span>
        <span class="ap-pill" :class="'ap-pill--' + statusOf(r).tone">{{ statusOf(r).label }}</span>
      </button>
    </div>

    <!-- Nothing searched yet -->
    <div v-if="!searched && !loading" class="ap-card">
      <div class="ap-empty">
        <span class="ap-empty-icon"><v-icon size="34">mdi-file-search-outline</v-icon></span>
        <div class="ap-empty-title">Find your application</div>
        <div>You can find the number on your acknowledgement and in My Applications.</div>
      </div>
    </div>

    <!-- Searching -->
    <div v-else-if="loading" class="ap-card">
      <div class="ap-empty">
        <v-progress-circular indeterminate color="#16a34a" size="44" width="4"></v-progress-circular>
        <div>Looking up {{ lastQuery }}…</div>
      </div>
    </div>

    <!-- Not found -->
    <div v-else-if="!app" class="ap-card">
      <div class="ap-empty">
        <span class="ap-empty-icon"><v-icon size="34">mdi-file-question-outline</v-icon></span>
        <div class="ap-empty-title">{{ message || "No application found" }}</div>
        <template v-if="suggestions.length">
          <div>Did you mean one of these?</div>
          <div class="tk-suggest">
            <button v-for="s in suggestions" :key="s._id" type="button" class="tk-suggest-item" @click="search(s.applicationNo)">
              <span class="tk-suggest-no">{{ s.applicationNo }}</span>
              <span class="tk-suggest-sub">{{ s.natureOfApplication }} · {{ formatDate(s.created_at) }}</span>
              <v-icon small color="#94a3b8">mdi-chevron-right</v-icon>
            </button>
          </div>
        </template>
        <div v-else>Check the number and try again.</div>
      </div>
    </div>

    <!-- Found -->
    <template v-else>
      <section class="ap-card tk-card">
        <div class="tk-top">
          <div class="tk-id">
            <span class="tk-no">{{ app.applicationNo }}</span>
            <span class="ap-pill" :class="'ap-pill--' + status.tone">{{ status.label }}</span>
            <span v-if="app.originalApplicationNo" class="tk-ext">
              <v-icon x-small>mdi-calendar-expand-horizontal</v-icon>
              Extension of {{ app.originalApplicationNo }}
            </span>
          </div>
          <button type="button" class="ap-btn ap-btn--outline ap-btn--sm" @click="openApp">
            <v-icon small>mdi-open-in-new</v-icon>
            Open application
          </button>
        </div>

        <!-- Rejected and licensed applications show how they ended, not who has them -->
        <div v-if="outcome" class="tk-now" :class="'tk-now--' + (outcome.kind === 'rejected' ? 'rejected' : 'licence')">
          <span class="tk-now-icon"><v-icon>{{ outcome.icon }}</v-icon></span>
          <div class="tk-now-text">
            <div class="tk-now-label">{{ outcome.kind === "rejected" ? "Outcome" : "Approved" }}</div>
            <div class="tk-now-who">{{ outcome.title }}</div>
            <div v-if="outcome.when" class="tk-now-detail">{{ formatDateTime(outcome.when) }}</div>
            <div v-if="outcome.note" class="tk-now-note">{{ outcome.note }}</div>
            <div v-if="outcome.flag" class="tk-now-flag">
              <v-icon x-small>mdi-timer-sand</v-icon>
              {{ outcome.flag }}
            </div>
          </div>
        </div>

        <!-- Where it is now -->
        <div v-else class="tk-now" :class="'tk-now--' + where.tone">
          <span class="tk-now-icon"><v-icon>{{ where.icon }}</v-icon></span>
          <div class="tk-now-text">
            <div class="tk-now-label">Currently with</div>
            <div class="tk-now-who">{{ where.who }}<span v-if="showOffice"> · {{ app.divisionName }}</span></div>
            <div v-if="where.detail" class="tk-now-detail">{{ where.detail }}</div>
          </div>
        </div>
        <p class="tk-next">{{ next }}</p>

        <!-- Four steps -->
        <ol v-if="app.status === 'Active'" class="ap-track tk-track">
          <li v-for="(s, i) in steps" :key="s.key" class="ap-track-step" :class="{ 'is-done': i < done, 'is-current': i === done }">
            <span class="ap-track-dot"><v-icon v-if="i < done" x-small>mdi-check</v-icon></span>
            {{ s.label }}
          </li>
        </ol>
      </section>

      <section class="ap-card">
        <div class="ap-card-head">
          <div class="ap-card-heading">
            <span class="ap-card-icon"><v-icon small>mdi-information-outline</v-icon></span>
            <div>
              <h2 class="ap-card-title">Application details</h2>
            </div>
          </div>
        </div>
        <div class="ap-card-body">
          <div class="ap-info">
            <div class="ap-info-item">
              <div class="ap-info-label">Production</div>
              <div class="ap-info-value">{{ filmName }}</div>
            </div>
            <div class="ap-info-item">
              <div class="ap-info-label">Category</div>
              <div class="ap-info-value">{{ app.natureOfApplication || "—" }}</div>
            </div>
            <div class="ap-info-item">
              <div class="ap-info-label">Division</div>
              <div class="ap-info-value">{{ app.divisionName || "—" }}</div>
            </div>
            <div class="ap-info-item">
              <div class="ap-info-label">Submitted</div>
              <div class="ap-info-value">{{ formatDate(app.created_at) }}</div>
            </div>
            <div class="ap-info-item">
              <div class="ap-info-label">Fees</div>
              <div class="ap-info-value">{{ fees }}</div>
            </div>
            <div class="ap-info-item">
              <div class="ap-info-label">Payment</div>
              <div class="ap-info-value">{{ payment }}</div>
            </div>
          </div>

          <div v-if="shoots.length" class="tk-shoots">
            <div class="ap-block-title">Shooting {{ shoots.length > 1 ? "locations" : "location" }}</div>
            <div v-for="(s, i) in shoots" :key="i" class="tk-shoot">
              <v-icon small color="#15803d">mdi-movie-open-outline</v-icon>
              <div class="tk-shoot-text">
                <b>{{ s.filmName || "Untitled" }}</b>
                <span>{{ s.location }}</span>
              </div>
              <span class="tk-shoot-dates">{{ formatDate(s.fromDate) }} – {{ formatDate(s.toDate) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- What has happened (only what the applicant is meant to see) -->
      <section class="ap-card">
        <div class="ap-card-head">
          <div class="ap-card-heading">
            <span class="ap-card-icon"><v-icon small>mdi-history</v-icon></span>
            <div>
              <h2 class="ap-card-title">Updates</h2>
              <p class="ap-card-sub">{{ events.length }} update{{ events.length === 1 ? "" : "s" }} so far</p>
            </div>
          </div>
        </div>
        <div class="ap-card-body">
          <ol class="tk-events">
            <li v-for="(e, i) in events" :key="i" class="tk-event" :class="'tk-event--' + e.tone">
              <span class="tk-event-dot"><v-icon x-small>{{ e.icon }}</v-icon></span>
              <div class="tk-event-body">
                <div class="tk-event-label">{{ e.label }}</div>
                <div class="tk-event-time">{{ formatDateTime(e.at) }}</div>
              </div>
            </li>
          </ol>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { APPLICANT_STEPS, stepsDone, nextStepText, outcomeOf, statusOf, whereIs } from "@/components/Common/applicantConfig";

// Track Application (applicant): one of the applicant's own applications by
// number — status, where it is and the dated updates they are meant to see.
// Officers' names and remarks are not part of it. The number is kept in the
// address (?no=) so a result can be reloaded.
export default {
  name: "ApplicantTrack",
  data() {
    return {
      query: "",
      lastQuery: "",
      loading: false,
      searched: false,
      app: null,
      events: [],
      message: "",
      suggestions: [],
      recent: [],
      steps: APPLICANT_STEPS,
    };
  },
  computed: {
    status() {
      return statusOf(this.app);
    },
    where() {
      return whereIs(this.app);
    },
    // How it ended (rejected / licence issued), with the rejection time taken
    // from the updates when the application doesn't carry it
    outcome() {
      const o = outcomeOf(this.app);
      if (o && o.kind === "rejected") {
        const rejection = this.events.filter((e) => e.key === "rejected").pop();
        if (rejection) o.when = rejection.at;
      }
      return o;
    },
    done() {
      return stepsDone(this.app);
    },
    next() {
      return nextStepText(this.app);
    },
    showOffice() {
      return !!this.app.divisionName && (this.where.tone === "office" || this.where.tone === "range");
    },
    shoots() {
      return (this.app && this.app.filmShootingDetails) || [];
    },
    filmName() {
      const first = this.shoots[0];
      return (first && first.filmName) || (this.app && this.app.natureOfApplication) || "—";
    },
    fees() {
      const a = this.app;
      if (!a || (!a.shootingfee && !a.installationfee)) return "Not fixed yet";
      const parts = ["₹" + this.money((a.shootingfee || 0) + (a.installationfee || 0))];
      if (a.securitydeposit) parts.push("deposit ₹" + this.money(a.securitydeposit));
      return parts.join(" · ");
    },
    payment() {
      const a = this.app;
      if (a.refundStatus === "Completed") return "Refunded";
      if (a.paymentStatus === "Success") return "Paid";
      return a.paymentInitiation === "YES" ? "Awaiting your payment" : "Not requested yet";
    },
  },
  created() {
    const no = this.$route.query.no;
    if (no) this.search(String(no));
    this.loadRecent();
  },
  methods: {
    statusOf,
    search(number) {
      const text = String(number || this.query || "").trim();
      if (!text) return;
      this.query = text;
      this.lastQuery = text;
      this.loading = true;
      this.searched = true;
      if (this.$route.query.no !== text) {
        this.$router.replace({ query: { ...this.$route.query, no: text } }).catch(() => {});
      }
      axios({
        method: "POST",
        url: "/my/track",
        headers: { token: localStorage.getItem("token") },
        data: { applicationNo: text },
      })
        .then((response) => {
          // Only the latest search may fill the page
          if (text !== this.lastQuery) return;
          this.loading = false;
          const res = response.data || {};
          if (res.status && res.data) {
            this.app = res.data.application;
            this.events = res.data.events || [];
            this.message = "";
            this.suggestions = [];
          } else {
            this.app = null;
            this.events = [];
            this.message = res.msg || "";
            this.suggestions = res.suggestions || [];
          }
        })
        .catch((err) => {
          if (text !== this.lastQuery) return;
          this.loading = false;
          this.app = null;
          this.events = [];
          this.suggestions = [];
          this.message = "Could not look up the application. Please try again.";
          console.error("Error tracking application:", err);
        });
    },
    reset() {
      this.query = "";
      this.lastQuery = "";
      this.searched = false;
      this.app = null;
      this.events = [];
      this.message = "";
      this.suggestions = [];
      if (this.$route.query.no) {
        const query = { ...this.$route.query };
        delete query.no;
        this.$router.replace({ query }).catch(() => {});
      }
    },
    // The applicant's latest applications, to pick from
    loadRecent() {
      axios({
        method: "POST",
        url: "/my/applications",
        headers: { token: localStorage.getItem("token") },
        data: { status: "yes", page: 1, limit: 6 },
      })
        .then((response) => {
          this.recent = response.data.status ? response.data.data || [] : [];
        })
        .catch(() => {});
    },
    openApp() {
      if (this.app) this.$router.push({ path: "/detailedviewapplicant", query: { id: this.app._id } });
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-IN");
    },
    formatDate(value) {
      return value ? moment(value).format("D MMM YYYY") : "—";
    },
    formatDateTime(value) {
      return value ? moment(value).format("D MMM YYYY, hh:mm A") : "";
    },
  },
};
</script>

<style scoped>
/* ── Search (inside the hero) ── */
.tk-hero {
  align-items: center;
}

.tk-search {
  display: flex;
  flex: 1 1 380px;
  max-width: 560px;
  gap: 10px;
}

.tk-search-box {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(233, 245, 238, 0.28);
  background: rgba(255, 255, 255, 0.1);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.tk-search-box:focus-within {
  border-color: #4ade80;
  background: rgba(255, 255, 255, 0.16);
}

.tk-search-box .v-icon {
  color: rgba(233, 245, 238, 0.8) !important;
}

.tk-search-input {
  flex: 1;
  min-width: 0;
  padding: 13px 0;
  border: none;
  outline: none;
  background: transparent;
  color: #f4faf6;
  font-family: inherit;
  font-size: 15px;
}

.tk-search-input::placeholder {
  color: rgba(233, 245, 238, 0.55);
}

.tk-search-clear {
  display: flex;
  padding: 2px;
  border: none;
  background: none;
  cursor: pointer;
}

/* ── Quick picks ── */
.tk-recent {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  margin-bottom: 18px;
}

.tk-recent-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--ap-muted);
}

.tk-recent-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px 6px 14px;
  border-radius: 999px;
  border: 1px solid var(--ap-line);
  background: #ffffff;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.tk-recent-chip:hover,
.tk-recent-chip:focus-visible {
  border-color: rgba(22, 163, 74, 0.5);
  background: #f4fbf7;
  outline: none;
}

.tk-recent-no {
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: var(--ap-gold);
}

/* ── Not found suggestions ── */
.tk-suggest {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 520px;
  margin-top: 4px;
}

.tk-suggest-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid var(--ap-line);
  background: #fbfdfc;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.tk-suggest-item:hover,
.tk-suggest-item:focus-visible {
  border-color: rgba(22, 163, 74, 0.5);
  background: #f4fbf7;
  outline: none;
}

.tk-suggest-no {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: var(--ap-gold);
}

.tk-suggest-sub {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  color: var(--ap-muted);
}

/* ── Result ── */
.tk-card {
  padding: 22px 24px 26px;
}

.tk-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.tk-id {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.tk-no {
  font-family: "Inter", sans-serif;
  font-size: 24px;
  font-weight: 500;
}

.tk-ext {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #fdf8ee;
  font-size: 12px;
  color: #8a6a32;
}

.tk-ext .v-icon {
  color: inherit !important;
}

.tk-now {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 18px 0 8px;
  padding: 16px 18px;
  border-radius: 18px;
  background: #ecfdf3;
  border: 1px solid #bbf7d0;
}

.tk-now-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 14px;
  background: #16a34a;
}

.tk-now-icon .v-icon {
  color: #ffffff !important;
}

.tk-now-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--ap-muted);
}

.tk-now-who {
  font-family: "Inter", sans-serif;
  font-size: 22px;
  line-height: 1.25;
  color: var(--ap-ink);
}

.tk-now-detail {
  font-size: 13.5px;
  color: #3f5a4a;
}

.tk-now--you {
  background: #fffbeb;
  border-color: #fde68a;
}

.tk-now--you .tk-now-icon {
  background: #d97706;
}

.tk-now--range {
  background: #f0f9ff;
  border-color: #bae6fd;
}

.tk-now--range .tk-now-icon {
  background: #0284c7;
}

.tk-now--rejected {
  background: #fef2f2;
  border-color: #fecaca;
}

.tk-now--rejected .tk-now-icon {
  background: #dc2626;
}

.tk-now-note {
  margin-top: 6px;
  font-size: 13.5px;
  line-height: 1.5;
  color: #475569;
}

.tk-now--rejected .tk-now-note {
  color: #b91c1c;
}

.tk-now-flag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #fef3c7;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
}

.tk-now-flag .v-icon {
  color: inherit !important;
}

.tk-now--closed {
  background: #f8fafc;
  border-color: #e2e8f0;
}

.tk-now--closed .tk-now-icon {
  background: #64748b;
}

.tk-next {
  margin: 4px 2px 20px;
  font-size: 14px;
  color: var(--ap-muted);
}

.tk-track {
  padding: 0 4px;
}

/* ── Details ── */
.tk-shoots {
  margin-top: 20px;
}

.tk-shoot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid var(--ap-line);
  background: #fbfdfc;
}

.tk-shoot-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  font-size: 13px;
  color: var(--ap-muted);
}

.tk-shoot-text b {
  font-size: 14.5px;
  color: var(--ap-ink);
}

.tk-shoot-dates {
  font-size: 13px;
  font-weight: 600;
  color: #14532d;
  white-space: nowrap;
}

/* ── Updates ── */
.tk-events {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tk-event {
  position: relative;
  display: flex;
  gap: 16px;
  padding-bottom: 20px;
}

.tk-event:last-child {
  padding-bottom: 0;
}

.tk-event::before {
  content: "";
  position: absolute;
  top: 30px;
  bottom: 0;
  left: 14px;
  width: 2px;
  background: var(--ap-line);
}

.tk-event:last-child::before {
  display: none;
}

.tk-event-dot {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #e0f2fe;
}

.tk-event-dot .v-icon {
  color: #0369a1 !important;
}

.tk-event--success .tk-event-dot {
  background: #dcfce7;
}

.tk-event--success .tk-event-dot .v-icon {
  color: #15803d !important;
}

.tk-event--warning .tk-event-dot {
  background: #fef3c7;
}

.tk-event--warning .tk-event-dot .v-icon {
  color: #a16207 !important;
}

.tk-event--danger .tk-event-dot {
  background: #fee2e2;
}

.tk-event--danger .tk-event-dot .v-icon {
  color: #b91c1c !important;
}

.tk-event--neutral .tk-event-dot {
  background: #f1f5f9;
}

.tk-event--neutral .tk-event-dot .v-icon {
  color: #475569 !important;
}

.tk-event-body {
  padding-top: 3px;
}

.tk-event-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--ap-ink);
}

.tk-event-time {
  margin-top: 2px;
  font-size: 12.5px;
  color: var(--ap-muted);
}

@media (max-width: 760px) {
  .tk-search {
    flex-basis: 100%;
    max-width: none;
  }

  .tk-card {
    padding: 18px 16px 20px;
  }

  .tk-now-who {
    font-size: 19px;
  }
}

@media (max-width: 600px) {
  .tk-search {
    flex-direction: column;
  }

  .tk-search .ap-btn {
    width: 100%;
  }

  .tk-shoot {
    flex-wrap: wrap;
  }

  .tk-shoot-dates {
    width: 100%;
    padding-left: 30px;
  }
}
</style>
